import React from 'react';
import { useBlocker } from 'react-router';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

interface UnsavedChangesBlockerProps {
  initialValues: Record<string, any>;
  currentValues: Record<string, any>;
  isSubmittingRef: React.RefObject<boolean>;
}

export default function UnsavedChangesBlocker({
  initialValues,
  currentValues,
  isSubmittingRef,
}: UnsavedChangesBlockerProps) {
  const [showUnsavedModal, setShowUnsavedModal] = React.useState(false);

  const hasUnsavedChanges = React.useMemo(() => {
    return Object.keys(initialValues).some((key) => {
      const currentValue = currentValues[key as keyof typeof currentValues];
      const initialValue = initialValues[key as keyof typeof initialValues];

      if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
        return (
          JSON.stringify(currentValue.sort()) !==
          JSON.stringify(initialValue.sort())
        );
      }

      return currentValue !== initialValue;
    });
  }, [currentValues, initialValues]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isSubmittingRef.current &&
      hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  React.useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowUnsavedModal(true);
    }
  }, [blocker.state]);

  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isSubmittingRef.current && hasUnsavedChanges) {
        e.preventDefault();
        return (e.returnValue = '');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isSubmittingRef]);

  return (
    <Dialog
      sx={{
        '& .MuiDialog-paper': {
          width: '80%',
          maxHeight: 435,
          borderRadius: 2.5,
        },
      }}
      maxWidth="xs"
      open={showUnsavedModal}
      onClose={() => {
        setShowUnsavedModal(false);
        blocker.reset?.();
      }}
    >
      <DialogTitle>Unsaved changes</DialogTitle>
      <DialogContent>
        You have unsaved changes on this page. Are you sure you want to navigate
        away?
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setShowUnsavedModal(false);
            blocker.proceed?.();
          }}
          color="primary"
          variant="contained"
        >
          OK
        </Button>
        <Button
          variant="text"
          color="secondary"
          onClick={() => {
            setShowUnsavedModal(false);
            blocker.reset?.();
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
